import type { SocialLoginCredentialInput } from '../data/repositories/SocialLogin';
import type { SocialProvider } from '../data/repositories/SocialLoginMethods';

const GOOGLE_GSI_SCRIPT_ID = 'google-gsi-script';
const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const KAKAO_SDK_SCRIPT_ID = 'kakao-sdk-script';
const KAKAO_SDK_SCRIPT_SRC = 'https://developers.kakao.com/sdk/js/kakao.min.js';
const APPLE_SDK_SCRIPT_ID = 'apple-signin-sdk-script';
const APPLE_SDK_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

let googleScriptPromise: Promise<void> | null = null;
let kakaoScriptPromise: Promise<void> | null = null;
let appleScriptPromise: Promise<void> | null = null;

function loadScript(scriptId: string, src: string): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (existingScript) {
    if (existingScript.dataset.loaded === 'true') {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function loadGoogleScript() {
  if (!googleScriptPromise) {
    googleScriptPromise = loadScript(GOOGLE_GSI_SCRIPT_ID, GOOGLE_GSI_SCRIPT_SRC);
  }
  return googleScriptPromise;
}

function loadKakaoScript() {
  if (!kakaoScriptPromise) {
    kakaoScriptPromise = loadScript(KAKAO_SDK_SCRIPT_ID, KAKAO_SDK_SCRIPT_SRC);
  }
  return kakaoScriptPromise;
}

function loadAppleScript() {
  if (!appleScriptPromise) {
    appleScriptPromise = loadScript(APPLE_SDK_SCRIPT_ID, APPLE_SDK_SCRIPT_SRC);
  }
  return appleScriptPromise;
}

function getGoogleClientId() {
  return process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
}

function getKakaoJavaScriptKey() {
  return (
    process.env.REACT_APP_KAKAO_JAVA_SCRIPT_KEY || process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY || ''
  );
}

function getAppleClientId() {
  return process.env.REACT_APP_APPLE_CLIENT_ID || '';
}

function getAppleRedirectUri() {
  const envValue = process.env.REACT_APP_APPLE_REDIRECT_URI?.trim() || '';
  const origin = typeof window !== 'undefined' ? window.location?.origin || '' : '';

  if (envValue) {
    if (/^https?:\/\//i.test(envValue)) {
      return envValue;
    }

    if (!origin) {
      return envValue;
    }

    const normalizedPath = envValue.startsWith('/') ? envValue : `/${envValue}`;
    return new URL(normalizedPath, origin).toString();
  }

  if (origin) {
    return `${origin}/auth/apple/callback`;
  }

  return '';
}

function createRandomString(length = 32) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);

    return Array.from(randomValues)
      .map((value) => alphabet[value % alphabet.length])
      .join('');
  }

  return Array.from({ length })
    .map(() => alphabet[Math.floor(Math.random() * alphabet.length)])
    .join('');
}

function loginWithGoogleIdToken(): Promise<SocialLoginCredentialInput | null> {
  const clientId = getGoogleClientId();
  if (!clientId) return Promise.resolve(null);

  return loadGoogleScript()
    .then(() => {
      const googleId = window.google?.accounts?.id;
      if (!googleId) return null;

      return new Promise<SocialLoginCredentialInput | null>((resolve) => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve(null);
        }, 15000);

        const settle = (value: SocialLoginCredentialInput | null) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          resolve(value);
        };

        googleId.cancel();
        googleId.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response?.credential) {
              settle({ idToken: response.credential });
              return;
            }
            settle(null);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true
        });

        googleId.prompt((notification) => {
          const isNotDisplayed = notification?.isNotDisplayed?.() ?? false;
          const isSkippedMoment = notification?.isSkippedMoment?.() ?? false;
          const isDismissedMoment = notification?.isDismissedMoment?.() ?? false;

          if (isNotDisplayed || isSkippedMoment || isDismissedMoment) {
            settle(null);
          }
        });
      });
    })
    .catch(() => null);
}

function loginWithGoogleAccessTokenPopup(): Promise<SocialLoginCredentialInput | null> {
  const clientId = getGoogleClientId();
  if (!clientId) return Promise.resolve(null);

  return loadGoogleScript()
    .then(() => {
      const googleOauth2 = window.google?.accounts?.oauth2;
      if (!googleOauth2) return null;

      return new Promise<SocialLoginCredentialInput | null>((resolve) => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve(null);
        }, 15000);

        const settle = (value: SocialLoginCredentialInput | null) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          resolve(value);
        };

        const tokenClient = googleOauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          callback: (response) => {
            if (response?.access_token) {
              settle({ accessToken: response.access_token });
              return;
            }

            settle(null);
          },
          error_callback: () => {
            settle(null);
          }
        });

        tokenClient.requestAccessToken({ prompt: 'select_account' });
      });
    })
    .catch(() => null);
}

function loginWithGoogleCredential(): Promise<SocialLoginCredentialInput | null> {
  return loginWithGoogleIdToken().then((credential) => {
    if (credential) return credential;
    return loginWithGoogleAccessTokenPopup();
  });
}

function loginWithKakaoAccessToken(): Promise<SocialLoginCredentialInput | null> {
  const appKey = getKakaoJavaScriptKey();
  if (!appKey) return Promise.resolve(null);

  return loadKakaoScript()
    .then(() => {
      if (!window.Kakao) return null;

      if (!window.Kakao.isInitialized()) {
        window.Kakao.init(appKey);
      }

      return new Promise<SocialLoginCredentialInput | null>((resolve) => {
        window.Kakao?.Auth.login({
          scope: 'account_email',
          throughTalk: false,
          success: (authObj) => {
            if (authObj?.access_token) {
              resolve({ accessToken: authObj.access_token });
              return;
            }
            resolve(null);
          },
          fail: () => {
            resolve(null);
          }
        });
      });
    })
    .catch(() => null);
}

function loginWithAppleCredential(): Promise<SocialLoginCredentialInput | null> {
  const clientId = getAppleClientId();
  const redirectUri = getAppleRedirectUri();
  if (!clientId || !redirectUri) return Promise.resolve(null);

  return loadAppleScript()
    .then(async () => {
      const appleAuth = window.AppleID?.auth;
      if (!appleAuth) return null;

      try {
        appleAuth.init({
          clientId,
          scope: 'name email',
          redirectURI: redirectUri,
          state: createRandomString(24),
          nonce: createRandomString(24),
          usePopup: true,
          responseType: 'code id_token'
        });

        const response = await appleAuth.signIn();
        const authorization = response?.authorization;
        if (!authorization) return null;

        return {
          ...(authorization.id_token ? { idToken: authorization.id_token } : {}),
          ...(authorization.code ? { authorizationCode: authorization.code } : {}),
          redirectUri
        };
      } catch (_error) {
        return null;
      }
    })
    .catch(() => null);
}

export function getSocialCredentialFromWeb(
  provider: SocialProvider
): Promise<SocialLoginCredentialInput | null> {
  if (provider === 'google') {
    return loginWithGoogleCredential();
  }

  if (provider === 'kakao') {
    return loginWithKakaoAccessToken();
  }

  if (provider === 'apple') {
    return loginWithAppleCredential();
  }

  return Promise.resolve(null);
}
